import React, { useState } from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'

import { _ } from 'utils'
import { $gray_900 } from 'utils/colors'
import { useEventSlug } from 'hooks'
import { Loader } from 'components'

import EditableTextField from '../FormFields/EditableTextField'
import EditableFileUpload, { VIDEOS_ONLY } from '../FormFields/EditableFileUpload'
import { BoothConfigModal, VideoData, VideoType } from '../types'
import { createEmbeddedVideo, updateVideo } from '../actions'
import { fetchBooth, selectBoothSlug } from '../fetchBoothSlice'
import useUpload from '../hooks/useUpload'
import VideoTypeSelect from './VideoTypeSelect'
import { getIsValidEmbeddedUrl, urlOptions } from '../embedded'

const S = {
    Container: styled.div`
        display: block;
        padding: 30px 0;
    `,
    Title: styled.h2`
        color: ${$gray_900};
        font-weight: bold;
        margin: auto auto 30px 0;
        padding: 0 0 0 0;
    `,
    Body: styled.div`
        display: block;
    `,
}

const VideoModal = ({ close, data }: BoothConfigModal) => {
    const video = data as VideoData
    const isEditing = !!video
    const initialType = video?.url ? VideoType.upload : VideoType.link

    const dispatch = useDispatch()
    const upload = useUpload('video')
    const eventSlug = useEventSlug()
    const boothSlug = useSelector(selectBoothSlug)

    const [isSaving, setSaving] = useState(false)
    const [name, setName] = useState<string>(video?.name)
    const [embeddedUrl, setEmbeddedUrl] = useState<string>(video?.embedded_url)
    const [videoType, setVideoType] = useState<VideoType>(initialType)
    const [newFile, setNetFile] = useState<File>(null)

    const isUploadType = videoType === VideoType.upload
    const isLinkType = videoType === VideoType.link
    const validUrl = embeddedUrl && getIsValidEmbeddedUrl(embeddedUrl)
    const validForm = (validUrl && isLinkType && name)
        || (isUploadType && name && (newFile || isEditing))

    const onFileSelect = (file: File) => {
        setName(file?.name || '')
        setNetFile(file)
    }

    const update = () => {
        if (isEditing && isUploadType) {
            return dispatch(updateVideo({
                id: video.id,
                data: { name, embedded_url: '' },
                eventSlug,
                boothSlug,
            }))
        }

        if (isEditing && isLinkType) {
            return dispatch(updateVideo({
                id: video.id,
                data: { name, embedded_url: embeddedUrl },
                eventSlug,
                boothSlug,
            }))
        }

        if (isUploadType) {
            return upload(newFile, name)
        }

        // Is not editing and link type
        return dispatch(createEmbeddedVideo({
            eventSlug,
            boothSlug,
            embeddedUrl,
            name,
        }))
    }

    const buttonUpdateClickHandler = async () => {
        setSaving(true)
        await update()
        await dispatch(fetchBooth({ eventSlug, boothSlug }))
        close?.()
    }

    return (
        <S.Container>
            <S.Body className="container">
                { !isEditing && (
                    <Row className="mb-4">
                        <Col md={12}>
                            <VideoTypeSelect
                                videoType={videoType}
                                setVideoType={setVideoType}
                            />
                        </Col>
                    </Row>
                )}

                { videoType === VideoType.link && (
                    <Row>
                        <Col md={12}>
                            <EditableTextField
                                defaultValue={name}
                                inputType="text"
                                label={_('booth.config.videos.item.name')}
                                onUpdate={setName}
                                tapToEdit={false}
                            />
                        </Col>
                        <Col md={12}>
                            <EditableTextField
                                defaultValue={embeddedUrl}
                                inputType="text"
                                label={_('booth.config.videos.item.link')}
                                onUpdate={setEmbeddedUrl}
                                tapToEdit={false}
                            />
                            { (embeddedUrl && !validUrl) && (
                                <span className="mb-2">
                                    The embedded link has to start with
                                    the link of one of the following providers:
                                    {' '}
                                    {urlOptions.join(', ')}
                                </span>
                            ) }
                        </Col>
                    </Row>
                ) }

                { videoType === VideoType.upload && (
                    <Row>
                        <Col md={12}>
                            { (newFile || isEditing) ? (
                                <EditableTextField
                                    defaultValue={name}
                                    inputType="text"
                                    label={_('booth.config.videos.item.name')}
                                    onUpdate={setName}
                                    tapToEdit={false}
                                />
                            ) : (
                                <EditableFileUpload
                                    accept={VIDEOS_ONLY}
                                    onFileSelect={onFileSelect}
                                />
                            )}
                        </Col>
                    </Row>
                ) }

                <Row>
                    {
                        isSaving
                            ? <Loader />
                            : (
                                <Col md={12}>
                                    <Button
                                        variant="primary mt-2 mr-2"
                                        onClick={buttonUpdateClickHandler}
                                        disabled={!validForm}
                                    >
                                        {_('form.save')}
                                    </Button>
                                    <Button
                                        variant="default mt-2"
                                        onClick={close}
                                    >
                                        {_('form.cancel')}
                                    </Button>
                                </Col>
                            )
                    }
                </Row>
            </S.Body>
        </S.Container>
    )
}

export default VideoModal
